import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader2, FileText, Send, ShieldCheck } from 'lucide-react';

const API_BASE = '/api/public';

interface PublicContract {
  contract_number: string;
  tenant_name: string;
  status: 'sent' | 'pending_signature' | 'signed' | 'active' | 'expired' | 'cancelled' | string;
  compiled_content: string | null;
  regenerated_compiled_content: string | null;
  regenerated_compiled_content_reason: string | null;
}

interface SignResult {
  signed_at?: string;
  signature_id?: string;
  pdf_url?: string;
  document_hash?: string;
}

export default function ContractSign() {
  const { token } = useParams<{ token: string }>();
  const [contract, setContract] = useState<PublicContract | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [step, setStep] = useState<'view' | 'otp_sent' | 'verifying' | 'signed' | 'error'>('view');
  const [otp, setOtp] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [signResult, setSignResult] = useState<SignResult | null>(null);

  useEffect(() => {
    if (token) void loadContract();
  }, [token]);

  async function loadContract() {
    try {
      const res = await fetch(`${API_BASE}/contracts/${token}`);
      const data = await res.json();
      if (data.success) {
        setContract(data.data);
        if (data.data.status === 'signed') setStep('signed');
      } else {
        setError(data.error || 'Contratto non trovato o scaduto');
        setStep('error');
      }
    } catch {
      setError('Impossibile caricare il contratto');
      setStep('error');
    } finally {
      setLoading(false);
    }
  }

  async function requestOtp() {
    setOtpLoading(true);
    setOtpError('');
    try {
      const res = await fetch(`${API_BASE}/contracts/${token}/request-otp`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setStep('otp_sent');
      } else {
        setOtpError(data.error || 'Errore invio OTP');
      }
    } catch (e) {
      setOtpError('Errore di connessione');
    } finally {
      setOtpLoading(false);
    }
  }

  async function verifyOtp() {
    if (otp.length !== 6) { setOtpError('Inserisci il codice a 6 cifre'); return; }
    setOtpLoading(true);
    setOtpError('');
    try {
      const res = await fetch(`${API_BASE}/contracts/${token}/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ otp })
      });
      const data = await res.json();
      if (data.success) {
        setSignResult(data.data);
        setStep('signed');
      } else {
        setOtpError(data.error || 'Codice non valido');
      }
    } catch (e) {
      setOtpError('Errore di connessione');
    } finally {
      setOtpLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="min-h-screen bg-muted/50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-foreground mb-2">Contratto Non Disponibile</h1>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (step === 'signed') {
    return (
      <div className="min-h-screen bg-muted/50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-foreground mb-2">Contratto Firmato</h1>
          <p className="text-muted-foreground mb-4">
            Il contratto <strong>{contract?.contract_number}</strong> è stato firmato con successo.
          </p>
          {signResult?.document_hash && (
            <div className="bg-muted rounded-lg p-3 mt-4">
              <p className="text-xs text-muted-foreground">Hash documento (SHA-256)</p>
              <p className="text-xs font-mono text-foreground break-all">{signResult.document_hash}</p>
            </div>
          )}
          <div className="mt-4 flex items-center justify-center gap-2 text-green-600">
            <ShieldCheck className="w-5 h-5" />
            <span className="text-sm">Firma elettronica semplice (art. 21 CAD)</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/50">
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <FileText className="w-6 h-6 text-blue-600" />
          <div>
            <h1 className="text-lg font-bold text-foreground">Contratto {contract?.contract_number}</h1>
            <p className="text-sm text-muted-foreground">{contract?.tenant_name}</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="relative z-0">
            {contract?.regenerated_compiled_content && (
              <div className="px-6 sm:px-8 pt-4 flex justify-end">
                <span
                  className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700"
                  title={contract.regenerated_compiled_content_reason || 'Versione rigenerata'}
                >
                  Versione rigenerata
                </span>
              </div>
            )}
            {(contract?.regenerated_compiled_content || contract?.compiled_content) ? (
              <iframe
                title={`contract-${contract?.contract_number || 'preview'}`}
                srcDoc={`<!DOCTYPE html><html lang="it"><head><meta charset="utf-8"><base target="_blank"></head><body style="margin:0;padding:24px;background:#fff;">${contract?.regenerated_compiled_content || contract?.compiled_content || ''}</body></html>`}
                sandbox="allow-same-origin allow-popups"
                className="w-full bg-white"
                style={{ height: '70vh', border: 0 }}
              />
            ) : (
              <div className="p-6 sm:p-8 text-center text-muted-foreground">
                <AlertCircle className="w-8 h-8 mx-auto mb-2 text-amber-500" />
                Contenuto contratto non disponibile.
              </div>
            )}
          </div>

          <div className="border-t bg-muted/50 p-6 relative z-10">
            {step === 'view' && (
              <div className="text-center">
                <p className="text-muted-foreground mb-4">
                  Prendi visione del contratto e clicca il pulsante per procedere alla firma.
                </p>
                <button
                  onClick={requestOtp}
                  disabled={otpLoading}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
                >
                  {otpLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  Firma Contratto
                </button>
                {otpError && <p className="text-red-600 mt-2 text-sm">{otpError}</p>}
              </div>
            )}

            {step === 'otp_sent' && (
              <div className="text-center max-w-sm mx-auto">
                <p className="text-muted-foreground mb-4">
                  Abbiamo inviato un codice di verifica a 6 cifre alla tua email registrata.
                  Inseriscilo qui sotto per completare la firma.
                </p>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="● ● ● ● ● ●"
                  className="w-full text-center text-3xl tracking-[0.5em] font-mono py-3 px-4 border-2 border-border rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-inset focus:ring-blue-200 outline-none"
                  style={{ position: 'relative', zIndex: 20, pointerEvents: 'auto', WebkitUserSelect: 'text', userSelect: 'text', color: '#111827', backgroundColor: '#ffffff', caretColor: '#3b82f6', fontSize: '1.875rem', letterSpacing: '0.5em' }}
                  autoFocus
                />
                <button
                  onClick={verifyOtp}
                  disabled={otpLoading || otp.length !== 6}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
                >
                  {otpLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />}
                  Conferma Firma
                </button>
                {otpError && <p className="text-red-600 mt-2 text-sm">{otpError}</p>}
                <button
                  onClick={requestOtp}
                  disabled={otpLoading}
                  className="mt-2 text-sm text-blue-600 hover:underline"
                >
                  Invia nuovo codice
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .contract-content { isolation: isolate; position: relative; z-index: 0; overflow: hidden; }
        .contract-content table { width: 100%; border-collapse: collapse; margin: 1em 0; }
        .contract-content th, .contract-content td { border: 1px solid #d1d5db; padding: 8px 12px; text-align: left; }
        .contract-content th { background: #f3f4f6; font-weight: 600; }
        .contract-content h1 { font-size: 1.5em; font-weight: bold; text-align: center; margin-bottom: 1em; }
        .contract-content h2 { font-size: 1.1em; font-weight: 600; margin-top: 1.5em; border-bottom: 1px solid #e5e7eb; padding-bottom: 0.3em; }
        input[inputmode="numeric"] { color: #111827 !important; background-color: #ffffff !important; -webkit-text-fill-color: #111827 !important; opacity: 1 !important; }
        input[inputmode="numeric"]::placeholder { color: #9ca3af !important; -webkit-text-fill-color: #9ca3af !important; opacity: 1 !important; }
      `}</style>
    </div>
  );
}
